/**
 * The Export window — what a person sees when they press Export.
 *
 * Left: the templates they may use ("As on screen" first when the designer
 * offers it; the organization's default preselected). Right: which records
 * (the list's tab + filters, re-read on the server with every read rule; the
 * selected ones; the ones loaded; or one record), which of the template's
 * forms (when it lets people choose), the format, the file name, a reason
 * when the screen asks for one, and the engine's preview.
 *
 * Small exports are ready at once (⤓ Download). Large ones are prepared in
 * the background: the window shows progress, and if the person closes it the
 * job keeps going and the file is saved when ready.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { Download, Loader2, Settings2, X } from 'lucide-react'
import { ErrorNote, Field, Note, Pill, Seg, input, meta, overlay, textarea, windowFrame, windowHeader } from '../../messaging/ui'
import { tw } from '../../testHooks'
import { Z_EXPORT_WINDOW } from '../../layers'
import { SheetPreview } from './SheetPreview'
import type {
  ExportFormat, ExportInfo, ExportJob, ExportPreview, ExportRequest, ExportTemplate, ExportTransport,
} from './types'
import { AS_ON_SCREEN_ID, EXPORT_CONFIG, exportErrorText, layoutSummary, saveBlob, templateForms } from './types'

export type ExportScope =
  | { kind: 'list'; query: Record<string, unknown>; matching?: number | null; loadedIds: string[];
      selectedIds: string[]; searchActive?: boolean }
  | { kind: 'record'; txnId: string; label?: string }

export interface ExportNotify {
  success: (m: string) => void
  error: (m: string) => void
  info?: (m: string) => void
}

type RowChoice = 'query' | 'selected' | 'loaded' | 'record'

/** A background job the window was closed on: poll it and save the file. */
function followInBackground(transport: ExportTransport, job: ExportJob, notify: ExportNotify) {
  let tries = 0
  const tick = async () => {
    tries += 1
    try {
      const j = await transport.job(job.job_id)
      if (j.status === 'running' && tries < 1800) { window.setTimeout(() => void tick(), 2000); return }
      if (j.status === 'done') {
        const blob = await transport.download(j.job_id)
        saveBlob(blob, j.filename || 'export')
        notify.success(`Your export is ready: ${j.filename} (${(j.records || 0).toLocaleString()} records).`)
      } else if (j.status === 'failed') {
        notify.error(j.error || 'The export failed.')
      }
    } catch (err) {
      notify.error(exportErrorText(err).message)
    }
  }
  window.setTimeout(() => void tick(), 2000)
}

export function ExportWindow({ transport, objName, scope, listColumns, onClose, notify, onManage, reloadKey }: {
  transport: ExportTransport
  objName: string
  scope: ExportScope
  /** The list's visible column keys — "As on screen". */
  listColumns: string[]
  onClose: () => void
  notify: ExportNotify
  /** Opens the template manager (only offered when the person may manage). */
  onManage?: (templateId?: string) => void
  /** Changes when the templates changed elsewhere (the manager saved one):
   *  the window re-reads them. */
  reloadKey?: number
}) {
  const [info, setInfo] = useState<ExportInfo | null>(null)
  const [loadError, setLoadError] = useState<{ message: string; problems: string[] } | null>(null)
  const [tplId, setTplId] = useState<string>('')
  const [rows, setRows] = useState<RowChoice>(
    scope.kind === 'record' ? 'record' : scope.selectedIds.length ? 'selected' : 'query')
  const [forms, setForms] = useState<string[] | null>(null)
  const [format, setFormat] = useState<ExportFormat>('xlsx')
  const [fileName, setFileName] = useState('')
  const [fileTouched, setFileTouched] = useState(false)
  const [reason, setReason] = useState('')
  const [job, setJob] = useState<ExportJob | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<{ message: string; problems: string[] } | null>(null)
  const [preview, setPreview] = useState<ExportPreview | null>(null)
  const [pvError, setPvError] = useState<string | null>(null)
  const [pvBusy, setPvBusy] = useState(false)
  const pvSeq = useRef(0)
  const jobRef = useRef<ExportJob | null>(null)
  const closedRef = useRef(false)

  const reload = useCallback(async () => {
    try {
      const i = await transport.info(objName)
      setInfo(i)
      setLoadError(null)
      setTplId((cur) => (cur && (cur === AS_ON_SCREEN_ID || i.templates.some(t => t.id === cur)) ? cur : i.default_template))
    } catch (err) {
      setLoadError(exportErrorText(err))
    }
  }, [transport, objName])
  useEffect(() => { void reload() }, [reload, reloadKey])

  const templates = useMemo<ExportTemplate[]>(() => {
    if (!info) return []
    const out: ExportTemplate[] = []
    if (info.action.offer_list_view) {
      out.push({
        id: AS_ON_SCREEN_ID, name: 'As on screen', description: 'The columns of your list, records only',
        is_default: false, form_choice: false, roles: [], format: 'xlsx', file_name: `${info.screen} {date}`,
        header: listColumns.map(c => ({ column: c, heading: c, value: 'display' as const })), details: [],
        layout: { mode: 'separate_sheets', header_on_lines: 'repeat_keys', records_without_lines: 'include',
          group_heading_row: false, header_sheet: '' },
        sheet: { freeze_header: true, filter_buttons: true, about_sheet: true }, scope: 'screen',
      })
    }
    return out.concat(info.templates)
  }, [info, listColumns])
  const tpl = templates.find(t => t.id === tplId) || null
  const allForms = useMemo(() => (tpl && info ? templateForms(tpl, info.catalog) : []), [tpl, info])
  const chosenForms = forms && forms.length ? forms.filter(f => allForms.includes(f)) : allForms
  const formChoice = !!tpl && tpl.form_choice !== false && allForms.length > 1

  // A new template resets the forms, format and (untouched) file name.
  useEffect(() => {
    if (!tpl || !info) return
    setForms(null)
    const allowed = info.action.formats
    setFormat(allowed.includes(tpl.format) ? tpl.format : allowed[0])
    if (!fileTouched) {
      const today = new Date().toISOString().slice(0, 10)
      setFileName((tpl.file_name || EXPORT_CONFIG.defaultFileName).replace('{date}', today).replace('{screen}', info.screen))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tplId, info])

  const request = useMemo<ExportRequest | null>(() => {
    if (!tpl) return null
    let r: ExportRequest['rows']
    if (scope.kind === 'record') r = { mode: 'ids', ids: [scope.txnId] }
    else if (rows === 'selected') r = { mode: 'ids', ids: scope.selectedIds }
    else if (rows === 'loaded') r = { mode: 'ids', ids: scope.loadedIds }
    else r = { mode: 'query', query: scope.query }
    return {
      obj: objName, template_id: tpl.id, format, rows: r,
      ...(tpl.id === AS_ON_SCREEN_ID ? { columns: listColumns } : {}),
      ...(formChoice && chosenForms.length < allForms.length ? { forms: chosenForms } : {}),
      ...(fileName.trim() ? { file_name: fileName.trim() } : {}),
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    }
  }, [tpl, scope, rows, objName, format, listColumns, formChoice, chosenForms, allForms, fileName, reason])

  // The engine preview of the first records, debounced.
  const pvKey = request ? JSON.stringify({ ...request, file_name: '', reason: '' }) : ''
  useEffect(() => {
    if (!request) return
    const my = ++pvSeq.current
    setPvBusy(true)
    const h = window.setTimeout(() => {
      transport.preview({ ...request, reason: request.reason || 'preview' }).then((p) => {
        if (my === pvSeq.current) { setPreview(p); setPvError(null) }
      }).catch((err) => {
        if (my === pvSeq.current) setPvError(exportErrorText(err).message)
      }).finally(() => { if (my === pvSeq.current) setPvBusy(false) })
    }, 350)
    return () => window.clearTimeout(h)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pvKey, transport])

  const count = scope.kind === 'record' ? 1
    : rows === 'selected' ? scope.selectedIds.length
      : rows === 'loaded' ? scope.loadedIds.length
        : (scope.matching ?? null)
  const cap = info?.max_rows || 0
  const big = count != null && info ? count > info.sync_max_rows : false

  // ONE step: a finished job's file is saved at once and the window closes.
  // The audit entry was written when the file was made.
  const finish = useCallback(async (j: ExportJob) => {
    try {
      const blob = await transport.download(j.job_id)
      saveBlob(blob, j.filename || 'export')
      notify.success(`Exported ${(j.records || 0).toLocaleString()} record${j.records === 1 ? '' : 's'} to ${j.filename}.`)
      for (const n of j.notices || []) notify.info?.(n)
      onClose()
    } catch (err) {
      setJob(null); jobRef.current = null
      setError(exportErrorText(err))
    }
  }, [transport, notify, onClose])

  // Poll a running (background) job while the window is open; save the file
  // the moment it is done.
  useEffect(() => {
    if (!job || job.status !== 'running') return
    const h = window.setTimeout(async () => {
      try {
        const j = await transport.job(job.job_id)
        if (closedRef.current) return
        setJob(j); jobRef.current = j
        if (j.status === 'done') void finish(j)
        else if (j.status === 'failed') setError({ message: j.error || 'The export failed.', problems: [] })
      } catch (err) {
        setError(exportErrorText(err))
        setJob(null)
      }
    }, 1500)
    return () => window.clearTimeout(h)
  }, [job, transport, finish])

  const start = async () => {
    if (!request) return
    if (info?.action.require_reason && !reason.trim()) {
      setError({ message: 'Say why you are exporting these records.', problems: [] })
      return
    }
    setBusy(true); setError(null)
    try {
      const j = await transport.start(request)
      setJob(j); jobRef.current = j
      if (j.status === 'done') await finish(j)
      else if (j.status === 'failed') setError({ message: j.error || 'The export failed.', problems: [] })
    } catch (err) {
      setError(exportErrorText(err))
    } finally {
      setBusy(false)
    }
  }
  const close = () => {
    closedRef.current = true
    const j = jobRef.current
    if (j && j.status === 'running') {
      notify.info?.('Your export is still being prepared. The file is saved when it is ready.')
      followInBackground(transport, j, notify)
    }
    onClose()
  }
  const cancelJob = async () => {
    if (!job) return
    try { await transport.cancel(job.job_id) } catch { /* the job may have finished */ }
    setJob(null)
  }

  const btnLabel = scope.kind === 'record' ? 'Export this record'
    : count == null ? 'Export'
      : `${big ? 'Prepare' : 'Export'} ${cap && count > cap ? `the first ${cap.toLocaleString()}` : count.toLocaleString()} record${count === 1 ? '' : 's'}`

  const recordCountText = (n: number | null | undefined) =>
    n == null ? 'All records' : `All ${n.toLocaleString()} record${n === 1 ? '' : 's'}`

  return (
    <div style={{ ...overlay, zIndex: Z_EXPORT_WINDOW }} role="dialog" aria-modal="true" aria-label="Export" {...tw('screen', 'export')}>
      <div style={{ ...windowFrame, maxWidth: 1180 }}>
        <div style={windowHeader}>
          <div style={{ flex: 1, minWidth: 0, fontSize: 17, fontWeight: 600 }}>
            Export {info ? info.screen : ''}{scope.kind === 'record' && scope.label ? ` · ${scope.label}` : ''}
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={close} aria-label="Close"
            {...tw('action', 'export-close')}><X size={15} /></button>
        </div>
        {loadError && <div style={{ padding: 16 }}><ErrorNote error={loadError} /></div>}
        {!info && !loadError && (
          <div style={{ ...meta, padding: 24, display: 'flex', gap: 8, alignItems: 'center' }}>
            <Loader2 size={15} className="spin" /> Loading…
          </div>
        )}
        {info && (
          <div style={{ display: 'flex', flexWrap: 'wrap', minHeight: 0 }}>
            <div style={{ flex: '0 1 320px', minWidth: 240, padding: '16px 18px', borderRight: '1px solid var(--color-border, #e6eaef)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                <span style={labelStyle}>Template</span>
                {info.can_manage && onManage && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => onManage(tpl?.scope === 'org' ? tpl.id : undefined)}
                    {...tw('action', 'export-manage-templates')}><Settings2 size={14} /> Manage templates</button>
                )}
              </div>
              <div style={{ display: 'grid', gap: 8 }} role="radiogroup" aria-label="Template">
                {templates.map(t => (
                  <label key={t.id} {...tw('action', `export:template:${t.id}`)}
                    style={tplCard(t.id === tplId)}>
                    <input type="radio" name="export-template" checked={t.id === tplId}
                      onChange={() => setTplId(t.id)}
                      style={{ width: 16, height: 16, marginTop: 3, accentColor: 'var(--color-accent, #0061D0)' }} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ fontWeight: 600, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        {t.name}
                        {t.id === info.default_template && t.id !== AS_ON_SCREEN_ID && <Pill tone="accent">Default</Pill>}
                        {t.scope === 'standard' && <Pill>Standard</Pill>}
                        {t.scope === 'org' && <Pill tone="success">Your organization</Pill>}
                      </span>
                      <span style={{ ...meta, display: 'block' }}>
                        {t.id === AS_ON_SCREEN_ID ? 'The columns of your list, records only'
                          : `${layoutSummary(t)} · ${t.header.length + t.details.reduce((n, d) => n + d.columns.length, 0)} columns`}
                      </span>
                    </span>
                  </label>
                ))}
                {!templates.length && <Note tone="warning">No template is available to you on this screen.</Note>}
              </div>
              {info.templates.length === 0 && !info.action.offer_list_view && (
                <p style={meta}>Ask your administrator for an export template.</p>
              )}
            </div>

            <div style={{ flex: '1 1 520px', minWidth: 0, padding: '16px 18px', display: 'grid', gap: 16,
              gridTemplateColumns: 'minmax(0, 1fr)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18 }}>
                <div style={{ flex: '1 1 260px' }}>
                  <span style={labelStyle}>Records</span>
                  {scope.kind === 'record' ? (
                    <div>This record{scope.label ? ` (${scope.label})` : ''}, with its lines.</div>
                  ) : (
                    <div style={{ display: 'grid', gap: 8 }} role="radiogroup" aria-label="Records">
                      <RowRadio on={rows === 'query'} onPick={() => setRows('query')} hook="export:rows:query"
                        title={recordCountText(scope.matching)}
                        hint="In this tab, with your filters. Read again on the server with your access." />
                      <RowRadio on={rows === 'selected'} onPick={() => setRows('selected')} hook="export:rows:selected"
                        disabled={!scope.selectedIds.length}
                        title={`The ${scope.selectedIds.length} record${scope.selectedIds.length === 1 ? '' : 's'} you selected`}
                        hint={scope.selectedIds.length ? 'Exactly these records.' : 'Tick records in the list first.'} />
                      <RowRadio on={rows === 'loaded'} onPick={() => setRows('loaded')} hook="export:rows:loaded"
                        title={`The ${scope.loadedIds.length} record${scope.loadedIds.length === 1 ? '' : 's'} loaded on screen`}
                        hint="What the list has loaded now." />
                      {scope.searchActive && rows === 'query' && (
                        <Note tone="warning">Your search box and column filters narrow only the loaded rows. To export exactly
                          what you see, tick those records and choose “you selected”.</Note>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ flex: '1 1 240px', display: 'grid', gap: 12, alignContent: 'start' }}>
                  <Field title="Format">
                    <Seg ariaLabel="Format" value={format} onChange={(v) => setFormat(v as ExportFormat)}
                      options={info.action.formats.map(f => ({ value: f,
                        label: EXPORT_CONFIG.formats.find(x => x.id === f)?.label || f }))} />
                  </Field>
                  <Field title="File name" htmlFor="export-file">
                    <input id="export-file" style={input} value={fileName}
                      maxLength={EXPORT_CONFIG.limits.maxFileNameLength}
                      onChange={(e) => { setFileName(e.target.value); setFileTouched(true) }}
                      {...tw('field', 'export:file-name')} />
                  </Field>
                </div>
              </div>

              {formChoice && info && tpl && (
                <div>
                  <span style={labelStyle}>Forms to export</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px' }}>
                    {allForms.map(f => {
                      const form = info.catalog.find(x => x.form_no === f)
                      const on = chosenForms.includes(f)
                      const last = on && chosenForms.length === 1
                      const n = f === info.catalog[0]?.form_no ? tpl.header.length
                        : (tpl.details.find(d => d.form_no === f)?.columns.length || 0)
                      return (
                        <label key={f} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: last ? 'not-allowed' : 'pointer' }}>
                          <input type="checkbox" checked={on} disabled={last}
                            onChange={(e) => setForms(e.target.checked
                              ? allForms.filter(x => x === f || chosenForms.includes(x))
                              : chosenForms.filter(x => x !== f))}
                            {...tw('field', `export:form:${f}`)}
                            style={{ width: 16, height: 16, marginTop: 3, accentColor: 'var(--color-accent, #0061D0)' }} />
                          <span>{form?.label}{form?.header ? ' (header)' : ''}
                            <span style={{ ...meta, display: 'block' }}>{n} columns{last ? ' · at least one form is exported' : ''}</span>
                          </span>
                        </label>
                      )
                    })}
                  </div>
                  {!chosenForms.includes(allForms[0]) && (
                    <p style={{ ...meta, margin: '6px 0 0' }}>Header left out: its key columns stay at the start of every line
                      row, so each line can be traced to its record.</p>
                  )}
                  {chosenForms.length === 1 && chosenForms[0] === allForms[0] && (
                    <p style={{ ...meta, margin: '6px 0 0' }}>Lines left out: one row per record.</p>
                  )}
                </div>
              )}

              {info.action.require_reason && (
                <Field title="Why are you exporting these records?" htmlFor="export-reason">
                  <textarea id="export-reason" style={{ ...textarea, minHeight: 60 }} value={reason}
                    onChange={(e) => setReason(e.target.value)} {...tw('field', 'export:reason')} />
                </Field>
              )}

              <div>
                <span style={labelStyle}>Preview</span>
                <SheetPreview preview={preview} busy={pvBusy} error={pvError} format={format}
                  emptyText="No records to show for these choices." />
              </div>
            </div>
          </div>
        )}
        {info && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
            padding: '12px 18px', borderTop: '1px solid var(--color-border, #d8dee6)', background: 'var(--color-surface-2, #f0f3f7)' }}>
            <div style={{ flex: '1 1 300px', minWidth: 0 }}>
              {error ? <ErrorNote error={error} /> : job?.status === 'done' ? (
                <span style={{ ...meta, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Loader2 size={14} className="spin" /> Saving {job.filename}…
                </span>
              ) : job?.status === 'running' ? (
                <span style={{ ...meta, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Loader2 size={14} className="spin" />
                  Preparing… {(job.records || 0).toLocaleString()}{job.total ? ` of ${job.total.toLocaleString()}` : ''} records.
                  You can close this window; the file is saved when it is ready.
                </span>
              ) : (
                <span style={meta}>{big ? `Over ${info.sync_max_rows.toLocaleString()} records the file is prepared in the background. ` : ''}
                  Your export is recorded with your name, the template and the record count.</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {job?.status === 'running' && (
                <button type="button" className="btn btn-secondary" onClick={() => void cancelJob()}>Stop</button>
              )}
              <button type="button" className="btn btn-secondary" onClick={close}>{job?.status === 'running' ? 'Close' : 'Cancel'}</button>
              <button type="button" className="btn btn-primary"
                disabled={busy || !tpl || job?.status === 'running' || job?.status === 'done'}
                onClick={() => void start()} {...tw('action', 'export:submit')}>
                {busy ? <Loader2 size={14} className="spin" /> : <Download size={15} />} {btnLabel}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const labelStyle: CSSProperties = {
  display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary, #465062)', marginBottom: 6,
}

function tplCard(on: boolean): CSSProperties {
  return {
    display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', alignItems: 'flex-start',
    border: `1px solid ${on ? 'var(--color-accent, #0061D0)' : 'var(--color-border-strong, #c9d1db)'}`,
    boxShadow: on ? 'inset 0 0 0 1px var(--color-accent, #0061D0)' : 'none',
    background: on ? 'var(--color-accent-weak, rgba(0,97,208,.10))' : 'var(--color-bg, #fff)',
  }
}

function RowRadio({ on, onPick, title, hint, disabled, hook }: {
  on: boolean; onPick: () => void; title: string; hint: string; disabled?: boolean; hook: string
}) {
  return (
    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1 }}>
      <input type="radio" name="export-rows" checked={on} disabled={disabled} onChange={onPick} {...tw('field', hook)}
        style={{ width: 16, height: 16, marginTop: 3, accentColor: 'var(--color-accent, #0061D0)' }} />
      <span>{title}<span style={{ ...meta, display: 'block' }}>{hint}</span></span>
    </label>
  )
}
